import { Controller } from "@hotwired/stimulus";
import { DirectUpload } from "@rails/activestorage";

const MAX_RECORDING_FILE_SIZE = 10 * 1024 * 1024;

export default class extends Controller {
  static targets = [
    "textarea",
    "tray",
    "trayLabel",
    "trayList",
    "limitWarning",
    "sendButton",
    "micButton",
    "photoInput",
    "fileInput",
    "form",
    "filesContainer",
    "audioContainer",
    "recordingUi",
    "recordingTimer",
    "recordingError",
  ];

  static values = {
    directUploadUrl: String,
    maxFiles: { type: Number, default: 5 },
    attachmentsLabel: { type: String, default: "" },
    removeLabel: { type: String, default: "" },
    addMoreLabel: { type: String, default: "" },
    maxDurationSeconds: { type: Number, default: 300 },
    micDeniedMessage: String,
    tooLongMessage: String,
    unsupportedMessage: String,
  };

  connect() {
    this.pendingFiles = [];
    this.updateSendState();
  }

  disconnect() {
    for (const entry of this.pendingFiles) {
      if (entry.previewUrl) URL.revokeObjectURL(entry.previewUrl);
    }
    if (this.mediaRecorder) this.mediaRecorder.onstop = null;
    this.stopRecordingTimer();
    this.stopMicStream();
  }

  // --- Attach popover entry points ---

  openPhotoPicker(event) {
    event.preventDefault();
    this.photoInputTarget.click();
  }

  openFilePicker(event) {
    event.preventDefault();
    this.fileInputTarget.click();
  }

  filesSelected(event) {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    this.addFiles(files);
  }

  addFiles(files) {
    for (const file of files) {
      if (this.pendingFiles.length >= this.maxFilesValue) {
        this.showLimitWarning();
        break;
      }

      this.hideLimitWarning();
      this.uploadFile(file);
    }
  }

  uploadFile(file) {
    const entry = { key: crypto.randomUUID(), file, signedId: null, uploading: true };
    if (file.type.startsWith("image/")) {
      entry.previewUrl = URL.createObjectURL(file);
    }
    this.pendingFiles.push(entry);
    this.renderTray();

    const upload = new DirectUpload(file, this.directUploadUrlValue);
    upload.create((error, attributes) => {
      if (error) {
        if (entry.previewUrl) {
          URL.revokeObjectURL(entry.previewUrl);
          entry.previewUrl = null;
        }
        this.pendingFiles = this.pendingFiles.filter((item) => item.key !== entry.key);
        this.renderTray();
        return;
      }

      entry.signedId = attributes.signed_id;
      entry.uploading = false;
      this.renderTray();
    });
  }

  removeAttachment(event) {
    const key = event.currentTarget.closest("[data-key]")?.dataset.key;
    const entry = this.pendingFiles.find((item) => item.key === key);
    if (entry?.previewUrl) {
      URL.revokeObjectURL(entry.previewUrl);
      entry.previewUrl = null;
    }
    this.pendingFiles = this.pendingFiles.filter((item) => item.key !== key);
    this.hideLimitWarning();
    this.renderTray();
  }

  clearAttachments(event) {
    event?.preventDefault();
    for (const entry of this.pendingFiles) {
      if (entry.previewUrl) URL.revokeObjectURL(entry.previewUrl);
    }
    this.pendingFiles = [];
    this.hideLimitWarning();
    this.renderTray();
  }

  renderTray() {
    this.trayListTarget.innerHTML = "";
    this.syncFileInputs();

    if (this.pendingFiles.length === 0) {
      this.trayTarget.classList.add("hidden");
      this.updateSendState();
      return;
    }

    this.trayTarget.classList.remove("hidden");
    this.trayLabelTarget.textContent = this.attachmentsLabelValue.replace(
      "__COUNT__",
      String(this.pendingFiles.length)
    );

    for (const entry of this.pendingFiles) {
      this.trayListTarget.appendChild(this.buildTrayItem(entry));
    }

    this.trayListTarget.appendChild(this.buildAddMoreTile());

    this.updateSendState();
  }

  buildTrayItem(entry) {
    return entry.previewUrl ? this.buildImageTrayItem(entry) : this.buildFileTrayItem(entry);
  }

  buildImageTrayItem(entry) {
    const item = document.createElement("div");
    item.className = "relative h-16 w-16 shrink-0 overflow-hidden rounded-ui border border-surface-border";
    item.dataset.key = entry.key;

    const img = document.createElement("img");
    img.src = entry.previewUrl;
    img.alt = entry.file.name;
    img.className = `h-full w-full object-cover${entry.uploading ? " opacity-50" : ""}`;

    item.appendChild(img);
    item.appendChild(
      this.buildRemoveButton("absolute right-0.5 top-0.5 rounded-ui-full bg-surface/80 leading-none")
    );

    return item;
  }

  buildFileTrayItem(entry) {
    const item = document.createElement("div");
    item.className =
      "relative flex h-16 w-32 shrink-0 items-center justify-between gap-2 rounded-ui border border-surface-border bg-surface px-2 py-1 text-xs";
    item.dataset.key = entry.key;

    const name = document.createElement("span");
    name.className = "min-w-0 flex-1 truncate text-content";
    name.textContent = entry.uploading ? `${entry.file.name} …` : entry.file.name;

    item.appendChild(name);
    item.appendChild(this.buildRemoveButton("shrink-0"));

    return item;
  }

  buildRemoveButton(extraClassName) {
    const removeButton = document.createElement("button");
    removeButton.type = "button";
    removeButton.className = ["text-content-muted", "hover:text-error", extraClassName].filter(Boolean).join(" ");
    removeButton.setAttribute("aria-label", this.removeLabelValue);
    removeButton.dataset.action = "click->chat-composer#removeAttachment";
    removeButton.textContent = "×";
    return removeButton;
  }

  buildAddMoreTile() {
    const tile = document.createElement("button");
    tile.type = "button";
    tile.className =
      "flex h-16 w-16 shrink-0 items-center justify-center rounded-ui border border-dashed border-surface-border text-content-muted hover:bg-surface-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500";
    tile.setAttribute("aria-label", this.addMoreLabelValue);
    tile.dataset.action = "click->chat-composer#openFilePicker";
    tile.textContent = "+";
    return tile;
  }

  // Ready (non-uploading) pending files as hidden `files[]` inputs inside the
  // composer form, so a native submit posts the same signed_ids the old
  // fetch-based `send()` sent as JSON -- the endpoint still reads
  // `Array(params[:files])` unchanged.
  syncFileInputs() {
    this.filesContainerTarget.innerHTML = "";

    for (const entry of this.pendingFiles) {
      if (entry.uploading || !entry.signedId) continue;

      const input = document.createElement("input");
      input.type = "hidden";
      input.name = "files[]";
      input.value = entry.signedId;
      this.filesContainerTarget.appendChild(input);
    }
  }

  showLimitWarning() {
    this.limitWarningTarget.classList.remove("hidden");
  }

  hideLimitWarning() {
    this.limitWarningTarget.classList.add("hidden");
  }

  // --- Textarea ---

  onInput() {
    this.autoGrow();
    this.updateSendState();
  }

  onKeydown(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      this.send();
    }
  }

  autoGrow() {
    this.textareaTarget.style.height = "auto";
    this.textareaTarget.style.height = `${this.textareaTarget.scrollHeight}px`;
  }

  insertEmoji(event) {
    const emoji = event.currentTarget.dataset.emoji;
    const textarea = this.textareaTarget;
    const start = textarea.selectionStart ?? textarea.value.length;
    const end = textarea.selectionEnd ?? textarea.value.length;

    textarea.value = `${textarea.value.slice(0, start)}${emoji}${textarea.value.slice(end)}`;
    textarea.focus();
    textarea.selectionStart = textarea.selectionEnd = start + emoji.length;

    this.autoGrow();
    this.updateSendState();
  }

  canSend() {
    const hasText = this.textareaTarget.value.trim().length > 0;
    const hasReadyAttachment = this.pendingFiles.some((item) => !item.uploading);
    const hasUploadingAttachment = this.pendingFiles.some((item) => item.uploading);

    return (hasText || hasReadyAttachment) && !hasUploadingAttachment;
  }

  updateSendState() {
    const hasContent = this.textareaTarget.value.trim().length > 0 || this.pendingFiles.length > 0;

    // "hidden" and "inline-flex" are both display utilities -- never let both
    // sit on the same button at once, or whichever compiles last in the
    // stylesheet wins regardless of which one was just toggled on.
    this.sendButtonTarget.classList.toggle("hidden", !hasContent);
    this.sendButtonTarget.classList.toggle("inline-flex", hasContent);
    this.micButtonTarget.classList.toggle("hidden", hasContent);
    this.micButtonTarget.classList.toggle("inline-flex", !hasContent);
    this.sendButtonTarget.disabled = !this.canSend();
  }

  // --- Send ---

  send(event) {
    event?.preventDefault();
    if (!this.canSend()) return;

    // Mirrors the old fetch-based send()'s `.trim()` on the JSON body --
    // native form submission would otherwise post the raw (untrimmed)
    // textarea value.
    this.textareaTarget.value = this.textareaTarget.value.trim();
    this.formTarget.requestSubmit();
  }

  // Turbo intercepts the form's native submit; the endpoint responds with an
  // empty 201/422 (no redirect, no turbo-stream), so Turbo just completes the
  // submission lifecycle without touching the DOM -- per D5 the composer only
  // clears on success, and the rendered message arrives via the existing
  // broadcast (no optimistic append).
  handleSubmitEnd(event) {
    if (event.detail.success) {
      this.clear();
    } else {
      // A failed audio submit leaves its signed_id behind in audioContainer;
      // purge it so a subsequent text/file send doesn't post stale audio
      // alongside the typed body (MessageForm#audio_exclusive would 422 it).
      this.audioContainerTarget.innerHTML = "";
    }
  }

  clear() {
    this.textareaTarget.value = "";
    this.autoGrow();
    for (const entry of this.pendingFiles) {
      if (entry.previewUrl) URL.revokeObjectURL(entry.previewUrl);
    }
    this.pendingFiles = [];
    this.hideLimitWarning();
    this.renderTray();
    this.audioContainerTarget.innerHTML = "";
    this.stopMicStream();
  }

  // --- Voice recording ---
  //
  // The mic button's `startRecording` click handler calls getUserMedia
  // synchronously (before any await) so the permission prompt still counts as
  // part of the user gesture on iOS Safari. The send button inside
  // `recordingUi` is wired to `stopRecording` -- it doubles as "stop and
  // send": while still recording it stops the recorder first and sends once
  // `onstop` hands back the blob; once the 300s auto-stop has already fired
  // it sends the already-captured blob immediately.

  async startRecording(event) {
    event?.preventDefault?.();
    this.clearRecordingError();

    if (!(window.MediaRecorder && navigator.mediaDevices?.getUserMedia)) {
      this.showRecordingError(this.unsupportedMessageValue);
      return;
    }

    try {
      this.micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (_error) {
      this.showRecordingError(this.micDeniedMessageValue);
      return;
    }

    this.recordedChunks = [];
    this.sendRequested = false;
    this.mediaRecorder = new MediaRecorder(this.micStream);
    this.mediaRecorder.ondataavailable = (dataEvent) => {
      if (dataEvent.data.size > 0) this.recordedChunks.push(dataEvent.data);
    };
    this.mediaRecorder.onstop = () => this.handleRecordingStopped();
    this.mediaRecorder.start();
    this.showRecordingUi();
  }

  stopRecording(event) {
    event?.preventDefault?.();
    if (this.sending) return;

    if (this.mediaRecorder && this.mediaRecorder.state === "recording") {
      this.sendRequested = true;
      this.mediaRecorder.stop();
    } else if (this.recordedBlob) {
      this.sendRecordedBlob(this.recordedBlob);
    }
  }

  cancelRecording(event) {
    event?.preventDefault?.();

    if (this.mediaRecorder) {
      this.mediaRecorder.onstop = null;
      if (this.mediaRecorder.state === "recording") this.mediaRecorder.stop();
    }
    this.stopMicStream();
    this.clearRecordingError();
    this.restoreIdleComposer();
  }

  handleRecordingStopped() {
    this.recordedBlob = new Blob(this.recordedChunks, { type: this.recordingMimeType() });
    this.stopMicStream();
    this.stopRecordingTimer();

    if (this.sendRequested) {
      this.sendRequested = false;
      this.sendRecordedBlob(this.recordedBlob);
    }
    // otherwise the 300s auto-stop already fired -- keep recordingUi visible
    // with the captured blob so the same send/cancel controls can act on it.
  }

  sendRecordedBlob(blob) {
    if (blob.size > MAX_RECORDING_FILE_SIZE) {
      this.showRecordingError(this.tooLongMessageValue);
      this.restoreIdleComposer();
      return;
    }

    this.sending = true;
    const extension = this.recordingMimeType() === "audio/webm" ? "webm" : "mp4";
    const file = new File([blob], `recording.${extension}`, { type: blob.type });
    const upload = new DirectUpload(file, this.directUploadUrlValue);

    upload.create((error, attributes) => {
      this.sending = false;

      if (error) {
        this.showRecordingError(error);
        this.restoreIdleComposer();
        return;
      }

      this.injectAudioInput(attributes.signed_id);
      this.restoreIdleComposer();
      this.formTarget.requestSubmit();
    });
  }

  injectAudioInput(signedId) {
    this.audioContainerTarget.innerHTML = "";

    const input = document.createElement("input");
    input.type = "hidden";
    input.name = "audio";
    input.value = signedId;
    this.audioContainerTarget.appendChild(input);
  }

  showRecordingUi() {
    this.recordingUiTarget.classList.remove("hidden");
    this.toggleInputRow(true);
    this.recordingStartedAt = Date.now();
    this.updateRecordingTimer();
    this.recordingIntervalId = window.setInterval(() => this.updateRecordingTimer(), 1000);
  }

  restoreIdleComposer() {
    this.recordingUiTarget.classList.add("hidden");
    this.toggleInputRow(false);
    this.stopRecordingTimer();
    if (this.hasRecordingTimerTarget) this.recordingTimerTarget.textContent = "0:00";
    this.mediaRecorder = null;
    this.recordedChunks = [];
    this.recordedBlob = null;
    this.sendRequested = false;
  }

  toggleInputRow(hide) {
    const inputRow = this.micButtonTarget.closest(".chat-composer-input");
    inputRow?.classList.toggle("hidden", hide);
  }

  updateRecordingTimer() {
    const elapsedSeconds = Math.floor((Date.now() - this.recordingStartedAt) / 1000);
    if (this.hasRecordingTimerTarget) {
      this.recordingTimerTarget.textContent = this.formatRecordingDuration(elapsedSeconds);
    }

    if (elapsedSeconds >= this.maxDurationSecondsValue && this.mediaRecorder?.state === "recording") {
      this.mediaRecorder.stop();
    }
  }

  stopRecordingTimer() {
    if (!this.recordingIntervalId) return;
    window.clearInterval(this.recordingIntervalId);
    this.recordingIntervalId = null;
  }

  formatRecordingDuration(seconds) {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = String(seconds % 60).padStart(2, "0");
    return `${minutes}:${remainingSeconds}`;
  }

  recordingMimeType() {
    if (window.MediaRecorder?.isTypeSupported?.("audio/webm")) return "audio/webm";
    return "audio/mp4";
  }

  showRecordingError(message) {
    if (!this.hasRecordingErrorTarget) return;
    this.recordingErrorTarget.textContent = message;
    this.recordingErrorTarget.classList.remove("hidden");
  }

  clearRecordingError() {
    if (!this.hasRecordingErrorTarget) return;
    this.recordingErrorTarget.textContent = "";
    this.recordingErrorTarget.classList.add("hidden");
  }

  stopMicStream() {
    this.micStream?.getTracks().forEach((track) => track.stop());
    this.micStream = null;
  }
}
